// ============================================================================
// BANKING (Receipts + Receipt Verification Worksheet + Bank Statements)
// ENDPOINTS — ADR-0024. BASE_PATHs match the backend controllers exactly:
//   ReceiptController          → [Route("api/v1/receipts")]
//   ReceiptWorksheetController → [Route("api/v1/receipt-assignments")]
//   BankStatementController    → [Route("api/v1/bank-statements")]
// ============================================================================

import { buildEndpoint, buildEndpointWithParams } from './base.endpoints';
import type { ID } from '../types';

const RECEIPT_BASE = '/receipts';
export const receiptEndpoints = {
  list: () => buildEndpoint(RECEIPT_BASE),
  getById: (id: ID) => buildEndpointWithParams(`${RECEIPT_BASE}/:id`, { id }),
  create: () => buildEndpoint(RECEIPT_BASE),
  assign: (id: ID) => buildEndpointWithParams(`${RECEIPT_BASE}/:id/assign`, { id }),
  /** Entity + BU tree for the assignment picker (ADR-0026 cross-entity routing). */
  assignmentTargets: () => buildEndpoint(`${RECEIPT_BASE}/assignment-targets`),
} as const;

const WORKSHEET_BASE = '/receipt-assignments';
export const receiptWorksheetEndpoints = {
  list: () => buildEndpoint(WORKSHEET_BASE),
  getById: (id: ID) => buildEndpointWithParams(`${WORKSHEET_BASE}/:id`, { id }),
  outstandingItems: () => buildEndpoint(`${WORKSHEET_BASE}/outstanding-items`),
  deductionReasons: () => buildEndpoint(`${WORKSHEET_BASE}/deduction-reasons`),
  saveAllocation: (id: ID) => buildEndpointWithParams(`${WORKSHEET_BASE}/:id/allocation`, { id }),
  post: (id: ID) => buildEndpointWithParams(`${WORKSHEET_BASE}/:id/post`, { id }),
  return: (id: ID) => buildEndpointWithParams(`${WORKSHEET_BASE}/:id/return`, { id }),
} as const;

const STATEMENT_BASE = '/bank-statements';
export const bankStatementEndpoints = {
  list: () => buildEndpoint(STATEMENT_BASE),
  getById: (id: ID) => buildEndpointWithParams(`${STATEMENT_BASE}/:id`, { id }),
  lines: () => buildEndpoint(`${STATEMENT_BASE}/lines`),
  inspect: () => buildEndpoint(`${STATEMENT_BASE}/inspect`),
  parse: () => buildEndpoint(`${STATEMENT_BASE}/parse`),
  import: () => buildEndpoint(STATEMENT_BASE),
  suggestions: (id: ID) => buildEndpointWithParams(`${STATEMENT_BASE}/:id/suggestions`, { id }),
  lineSuggestion: (lineId: ID) => buildEndpointWithParams(`${STATEMENT_BASE}/lines/:lineId/suggestion`, { lineId }),
  lineMatch: (lineId: ID) => buildEndpointWithParams(`${STATEMENT_BASE}/lines/:lineId/match`, { lineId }),
  matches: () => buildEndpoint(`${STATEMENT_BASE}/matches`),
  bookEntries: () => buildEndpoint(`${STATEMENT_BASE}/book-entries`),
  unmatchLine: (lineId: ID) => buildEndpointWithParams(`${STATEMENT_BASE}/lines/:lineId/unmatch`, { lineId }),
  ignoreLine: (lineId: ID) => buildEndpointWithParams(`${STATEMENT_BASE}/lines/:lineId/ignore`, { lineId }),
  postReceiptVoucher: (lineId: ID) => buildEndpointWithParams(`${STATEMENT_BASE}/lines/:lineId/post-receipt-voucher`, { lineId }),
  postPaymentVoucher: (lineId: ID) => buildEndpointWithParams(`${STATEMENT_BASE}/lines/:lineId/post-payment-voucher`, { lineId }),
  postQuick: (lineId: ID) => buildEndpointWithParams(`${STATEMENT_BASE}/lines/:lineId/post-quick`, { lineId }),
  createReceiptFromLine: (lineId: ID) => buildEndpointWithParams(`${STATEMENT_BASE}/lines/:lineId/create-receipt`, { lineId }),
  formats: () => buildEndpoint(`${STATEMENT_BASE}/formats`),
  updateFormat: (id: ID) => buildEndpointWithParams(`${STATEMENT_BASE}/formats/:id`, { id }),
} as const;

const PAYMENT_MODE_BASE = '/payment-modes';
export const paymentModeEndpoints = {
  list: () => buildEndpoint(PAYMENT_MODE_BASE),
  update: (id: ID) => buildEndpointWithParams(`${PAYMENT_MODE_BASE}/:id`, { id }),
  rules: () => buildEndpoint(`${PAYMENT_MODE_BASE}/charge-rules`),
  rule: (id: ID) => buildEndpointWithParams(`${PAYMENT_MODE_BASE}/charge-rules/:id`, { id }),
  stats: () => buildEndpoint(`${PAYMENT_MODE_BASE}/charge-stats`),
} as const;

// BankReconciliationController → [Route("api/v1/bank-reconciliation")] (ADR-0055 §7-8)
const BANK_RECONCILIATION_BASE = '/bank-reconciliation';

export const bankReconciliationEndpoints = {
  start: () => buildEndpoint(`${BANK_RECONCILIATION_BASE}/start`),
  statement: () => buildEndpoint(`${BANK_RECONCILIATION_BASE}/statement`),
  statementExport: () => buildEndpoint(`${BANK_RECONCILIATION_BASE}/statement/export`),
} as const;
